import { ApiError, AuthError, appPath, toProblem, withBase, type AuthAnswer, type SignIn, type TenantAuth } from '@hive/api';
import { Icon, Spinner, useErrorText, useToast } from '@hive/ui';
import QRCode from 'qrcode';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useT } from '../../i18n';

type Step =
  | { kind: 'credentials' }
  | { kind: 'forgot'; sent: boolean }
  | { kind: 'mfa'; challenge: string }
  | { kind: 'enroll'; challenge: string; uri: string; secret: string }
  | { kind: 'recovery'; codes: string[] }
  | { kind: 'link'; mode: 'invite' | 'reset'; token: string; email?: string; invalid?: string };

/** O link do e-mail traz o token no fragmento (#), que não vai a servidores nem aos registros. */
function linkStep(): Step | null {
  const p = appPath();
  const token = window.location.hash.replace(/^#/, '');
  if ((p === '/auth/invite' || p === '/auth/reset') && token) {
    return { kind: 'link', mode: p === '/auth/invite' ? 'invite' : 'reset', token };
  }
  return null;
}

/** Texto do erro para a pessoa: o do núcleo (já no idioma dela) ou o nosso, nunca "HTTP 502" nem a exceção crua. */
function message(e: unknown, errText: (err: unknown) => string): string {
  if (e instanceof AuthError) {
    const fields = e.problem.fields as Record<string, string> | undefined;
    const first = fields ? Object.values(fields)[0] : undefined;
    return first ? String(first) : errText(new ApiError(e.status, toProblem(e.status, e.problem)));
  }
  return errText(e);
}

/** Passos da entrada: senha → código (ou cadastro do autenticador) → códigos de recuperação; convite e redefinição. */
export function SignInSteps({
  auth,
  tenant,
  methods,
  pending,
  onDone,
  onSso,
}: {
  auth: TenantAuth;
  tenant: SignIn;
  methods: Set<string>;
  pending?: AuthAnswer | null;
  onDone: () => void;
  onSso: (idp: 'oidc' | 'saml') => void;
}) {
  const t = useT();
  const errText = useErrorText();
  const toast = useToast();
  const [step, setStep] = useState<Step>(() => linkStep() ?? { kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const next = (a: AuthAnswer) => {
    setCode('');
    setError('');
    if (a.status === 'mfa') setStep({ kind: 'mfa', challenge: a.challenge ?? '' });
    else if (a.status === 'enroll') setStep({ kind: 'enroll', challenge: a.challenge ?? '', uri: a.otpauthUri ?? '', secret: a.secret ?? '' });
    else if (a.recoveryCodes?.length) setStep({ kind: 'recovery', codes: a.recoveryCodes });
    else {
      if (appPath().startsWith('/auth/')) window.history.replaceState(null, '', withBase('/'));
      onDone();
    }
  };

  useEffect(() => {
    if (pending) next(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só a resposta vinda do provedor
  }, [pending]);

  // link de convite/redefinição: confere para quem é antes de pedir a senha
  const linkToken = step.kind === 'link' ? step.token : null;
  const linkMode = step.kind === 'link' ? step.mode : null;
  useEffect(() => {
    if (!linkToken || !linkMode) return;
    (linkMode === 'invite' ? auth.inviteInfo(linkToken) : auth.resetInfo(linkToken))
      .then((r) => setStep({ kind: 'link', mode: linkMode, token: linkToken, email: r.email }))
      .catch((e) => setStep({ kind: 'link', mode: linkMode, token: linkToken, invalid: message(e, errText) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- errText muda a cada desenho; o texto vale no idioma atual
  }, [auth, linkToken, linkMode]);

  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await f();
    } catch (e) {
      setError(message(e, errText));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent, f: () => Promise<void>) => {
    e.preventDefault();
    void run(f);
  };

  const err = error ? (
    <p className="field-err" role="alert">
      {error}
    </p>
  ) : null;

  if (step.kind === 'mfa')
    return (
      <form className="stack-sm" onSubmit={(e) => submit(e, async () => next(await auth.mfa(step.challenge, code)))}>
        <h2 style={{ fontSize: 17, margin: 0 }}>{t('signin.mfa.title')}</h2>
        <p className="small muted">{t('signin.mfa.text')}</p>
        <div className="field">
          <label htmlFor="si-code">{t('signin.mfa.code')}</label>
          <input id="si-code" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={12} />
        </div>
        {err}
        <button type="submit" className="btn btn-block btn-lg btn-primary" disabled={busy || code.trim().length < 6}>
          {t('signin.verify')}
        </button>
        <span className="hint">{t('signin.mfa.recoveryHint')}</span>
        <button type="button" className="link-btn small" onClick={() => setStep({ kind: 'credentials' })}>
          {t('signin.back')}
        </button>
      </form>
    );

  if (step.kind === 'enroll')
    return (
      <form className="stack-sm" onSubmit={(e) => submit(e, async () => next(await auth.enroll(step.challenge, code)))}>
        <h2 style={{ fontSize: 17, margin: 0 }}>{t('signin.enroll.title')}</h2>
        <p className="small muted">{t('signin.enroll.text')}</p>
        <Qr uri={step.uri} label={t('signin.enroll.qr')} />
        <span className="small">{t('signin.enroll.manual')}</span>
        <code className="mono small" style={{ overflowWrap: 'anywhere' }}>
          {step.secret.replace(/(.{4})/g, '$1 ').trim()}
        </code>
        <div className="field">
          <label htmlFor="si-ecode">{t('signin.mfa.code')}</label>
          <input id="si-ecode" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} />
        </div>
        {err}
        <button type="submit" className="btn btn-block btn-lg btn-primary" disabled={busy || code.trim().length !== 6}>
          {t('signin.verify')}
        </button>
      </form>
    );

  if (step.kind === 'recovery')
    return (
      <div className="stack-sm">
        <h2 style={{ fontSize: 17, margin: 0 }}>{t('signin.recovery.title')}</h2>
        <p className="small muted">{t('signin.recovery.text')}</p>
        <ul className="mono small recovery-codes">
          {step.codes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <button
          type="button"
          className="btn"
          onClick={() => void navigator.clipboard?.writeText(step.codes.join('\n')).then(() => toast(t('signin.recovery.copied'), { kind: 'ok' }))}
        >
          {t('signin.recovery.copy')}
        </button>
        <button type="button" className="btn btn-block btn-lg btn-primary" onClick={() => next({ status: 'ok' })}>
          {t('signin.recovery.done')}
        </button>
      </div>
    );

  if (step.kind === 'forgot')
    return (
      <form className="stack-sm" onSubmit={(e) => submit(e, async () => (await auth.requestReset(email), setStep({ kind: 'forgot', sent: true })))}>
        <p className="small muted">{t('signin.forgotText')}</p>
        <div className="field">
          <label htmlFor="si-femail">{t('signin.email')}</label>
          <input id="si-femail" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        {step.sent && (
          <p className="small" role="status">
            {t('signin.forgotSent')}
          </p>
        )}
        {err}
        <button type="submit" className="btn btn-block btn-primary" disabled={busy || !email.includes('@')}>
          {t('signin.forgotSend')}
        </button>
        <button type="button" className="link-btn small" onClick={() => setStep({ kind: 'credentials' })}>
          {t('signin.back')}
        </button>
      </form>
    );

  if (step.kind === 'link') {
    if (step.invalid)
      return (
        <div className="stack-sm">
          <p className="field-err" role="alert">
            {step.invalid}
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              window.history.replaceState(null, '', '/');
              setStep({ kind: 'credentials' });
            }}
          >
            {t('signin.back')}
          </button>
        </div>
      );
    if (!step.email) return <Spinner label={t('signin.linkChecking')} />;
    const mismatch = password2.length > 0 && password !== password2;
    return (
      <form
        className="stack-sm"
        onSubmit={(e) =>
          submit(e, async () => next(step.mode === 'invite' ? await auth.acceptInvite(step.token, password) : await auth.reset(step.token, password)))
        }
      >
        <h2 style={{ fontSize: 17, margin: 0 }}>{step.mode === 'invite' ? t('signin.invite.title') : t('signin.reset.title')}</h2>
        <p className="small muted">
          {step.mode === 'invite' ? t('signin.invite.text', { company: tenant.name ?? '', email: step.email }) : t('signin.reset.text', { email: step.email })}
        </p>
        <input type="email" autoComplete="username" value={step.email} readOnly hidden />
        <div className="field">
          <label htmlFor="si-np">{t('signin.newPassword')}</label>
          <input id="si-np" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-describedby="si-rules" />
          <span className="hint" id="si-rules">
            {t('signin.passwordRules')}
          </span>
        </div>
        <div className="field">
          <label htmlFor="si-np2">{t('signin.confirmPassword')}</label>
          <input id="si-np2" type="password" autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} aria-invalid={mismatch} />
          {mismatch && <span className="field-err">{t('signin.mismatch')}</span>}
        </div>
        {err}
        <button type="submit" className="btn btn-block btn-lg btn-primary" disabled={busy || password.length < 12 || password !== password2}>
          {t('signin.setPassword')}
        </button>
      </form>
    );
  }

  return (
    <>
      {(methods.has('oidc') || methods.has('saml')) && (
        <div className="stack-sm">
          {(['oidc', 'saml'] as const)
            .filter((m) => methods.has(m))
            .map((m) => (
              <button key={m} type="button" className="sso-btn" onClick={() => onSso(m)}>
                <span className="sso-logo" aria-hidden="true">
                  <Icon name={m === 'oidc' ? 'shield' : 'key'} />
                </span>
                {tenant.sso?.find((x) => x.method === m)?.displayName ?? t(m === 'oidc' ? 'signin.sso' : 'signin.saml')}
              </button>
            ))}
        </div>
      )}
      {(methods.has('oidc') || methods.has('saml')) && (methods.has('local') || methods.has('ldap')) && <div className="or">{t('signin.or')}</div>}
      {(methods.has('local') || methods.has('ldap')) && (
        <form className="stack-sm" onSubmit={(e) => submit(e, async () => next(await auth.login(email, password)))}>
          <div className="field">
            <label htmlFor="si-email">{t('signin.email')}</label>
            <input id="si-email" type={methods.has('ldap') ? 'text' : 'email'} autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="si-pw">{t('signin.password')}</label>
            <input id="si-pw" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {err}
          <button type="submit" className="btn btn-block btn-lg btn-primary" disabled={busy || !email || !password}>
            <Icon name="lock" />
            {t('signin.submit')}
          </button>
          <button type="button" className="link-btn small" onClick={() => setStep({ kind: 'forgot', sent: false })}>
            {t('signin.forgot')}
          </button>
        </form>
      )}
      {methods.has('ldap') && <span className="hint">{t('signin.ldapNote')}</span>}
      {methods.size === 0 && <p className="muted">{t('signin.noMethods')}</p>}
      <p className="hint">{methods.has('local') ? t('signin.localHint') : t('signin.methodsHint')}</p>
    </>
  );
}

function Qr({ uri, label }: { uri: string; label: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (ref.current && uri) void QRCode.toCanvas(ref.current, uri, { width: 200, margin: 1 });
  }, [uri]);
  return <canvas ref={ref} role="img" aria-label={label} className="qr" />;
}
